// Appels sortants vers les prestataires (Beta Connect, Kashy) : nouvelles tentatives
// exponentielles sur erreur réseau, délai dépassé, 429 et 5xx ; disjoncteur en mémoire ;
// signature HMAC-SHA256 horodatée des webhooks. Aucune dépendance à un runtime : `fetch`,
// l'attente et l'horloge sont injectés.

export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** Disjoncteur ouvert : l'appel n'a pas été tenté. */
export class CircuitOpenError extends Error {
  override name = 'CircuitOpenError';
}

export interface RetryOptions {
  /** Tentatives au total (3 par défaut, spec 06). */
  attempts?: number;
  baseDelayMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const isRetryableStatus = (status: number) => status === 429 || status >= 500;

export async function fetchWithRetry(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
  options: RetryOptions = {},
): Promise<Response> {
  const attempts = options.attempts ?? 3;
  const base = options.baseDelayMs ?? 200;
  const sleep = options.sleep ?? defaultSleep;
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(base * 2 ** (attempt - 1));
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 10_000);
    try {
      const res = await fetchImpl(url, { ...init, signal: controller.signal });
      if (!isRetryableStatus(res.status) || attempt === attempts - 1) return res;
      lastError = new HttpError(res.status, null, `HTTP ${res.status}`);
    } catch (error) {
      lastError = error;
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/**
 * Disjoncteur par instance : après `threshold` échecs consécutifs, les appels sont refusés
 * pendant `cooldownMs`, puis un appel d'essai est laissé passer. Protège le prestataire
 * pendant une panne ; le disjoncteur métier par tenant (spec 06) vit dans l'API.
 */
export class CircuitBreaker {
  private failures = 0;
  private openedAt: number | null = null;

  constructor(
    private readonly options: { threshold?: number; cooldownMs?: number; now?: () => number } = {},
  ) {}

  get state(): 'closed' | 'open' | 'half_open' {
    if (this.openedAt === null) return 'closed';
    return this.now() - this.openedAt >= (this.options.cooldownMs ?? 30_000) ? 'half_open' : 'open';
  }

  async run<T>(call: () => Promise<T>): Promise<T> {
    if (this.state === 'open') throw new CircuitOpenError('Prestataire momentanément indisponible');
    try {
      const result = await call();
      this.failures = 0;
      this.openedAt = null;
      return result;
    } catch (error) {
      if (!(error instanceof HttpError) || isRetryableStatus(error.status)) {
        this.failures++;
        if (this.failures >= (this.options.threshold ?? 5) || this.state === 'half_open') {
          this.openedAt = this.now();
        }
      }
      throw error;
    }
  }

  private now() {
    return this.options.now?.() ?? Date.now();
  }
}

// --- Signature des webhooks : `t=<secondes unix>,v1=<hex HMAC-SHA256(secret, "<t>.<corps>")>` ---

const encoder = new TextEncoder();

async function hmacHex(secret: string, payload: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(payload)));
  return [...mac].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function signWebhook(
  secret: string,
  body: string,
  timestamp: number,
): Promise<string> {
  return `t=${timestamp},v1=${await hmacHex(secret, `${timestamp}.${body}`)}`;
}

/** Tolérance d'horodatage contre le rejeu (spec 06 : 5 minutes). */
export const WEBHOOK_TOLERANCE_SECONDS = 300;

export async function verifyWebhookSignature(
  secret: string,
  header: string | null | undefined,
  body: string,
  nowSeconds: number,
  toleranceSeconds = WEBHOOK_TOLERANCE_SECONDS,
): Promise<boolean> {
  if (!header) return false;
  const parts = Object.fromEntries(
    header.split(',').map((p) => {
      const i = p.indexOf('=');
      return [p.slice(0, i).trim(), p.slice(i + 1).trim()];
    }),
  );
  const t = Number(parts.t);
  const v1 = parts.v1;
  if (!Number.isInteger(t) || !v1 || !/^[0-9a-f]{64}$/.test(v1)) return false;
  if (Math.abs(nowSeconds - t) > toleranceSeconds) return false;
  const expected = await hmacHex(secret, `${t}.${body}`);
  let diff = 0;
  for (let i = 0; i < expected.length; i++) diff |= expected.charCodeAt(i) ^ v1.charCodeAt(i);
  return diff === 0;
}
